import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  InputAdornment,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DashboardLayout from "../../layout/DashboardLayout";
import { processPccRequest, rejectPccRequest } from "../../api/auth";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import { brand } from "../../theme";

export default function ProcessPccPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    pccCode: "",
    epr: "",
    password: "",
    notes: "",
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleApprove = async () => {
    if (!form.pccCode) {
      toast.error("PCC Code is required to approve");
      return;
    }
    try {
      setLoading(true);
      const res = await processPccRequest(id, form);
      if (res.data.success) {
        toast.success("PCC approved successfully");
        navigate(-1);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to approve PCC");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      const res = await rejectPccRequest(id, { notes: form.notes });
      if (res.data.success) {
        toast.success("PCC request rejected");
        navigate(-1);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reject PCC request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <Box mb={3}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ color: "#64748b", mb: 1.5, px: 0, "&:hover": { bgcolor: "transparent", color: brand.terracotta } }}
        >
          Back
        </Button>
        <Typography variant="h5" sx={{ color: "#0f172a" }}>
          Process PCC Request
        </Typography>
        <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
          Assign a Pseudo City Code or reject the request with a reason.
        </Typography>
      </Box>

      <Box maxWidth={640}>
        <Paper>
          {/* Header strip */}
          <Box
            sx={{
              px: 3,
              py: 2,
              bgcolor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              borderRadius: "12px 12px 0 0",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                bgcolor: brand.terracottaTint,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FlightTakeoffIcon sx={{ color: brand.terracotta, fontSize: "1.1rem" }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ color: "#0f172a" }}>
                PCC Details
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Request ID: {id}
              </Typography>
            </Box>
          </Box>

          <Box p={3}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Pseudo City Code *"
                  name="pccCode"
                  fullWidth
                  size="medium"
                  value={form.pccCode}
                  onChange={handleChange}
                  placeholder="e.g. LON1A"
                  helperText="Required to approve"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="EPR"
                  name="epr"
                  fullWidth
                  size="medium"
                  value={form.epr}
                  onChange={handleChange}
                  placeholder="e.g. SABRE123"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  size="medium"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="GDS login password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Notes / Rejection Reason"
                  name="notes"
                  multiline
                  rows={4}
                  fullWidth
                  size="medium"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Optional for approval. Required context when rejecting."
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Alert
              severity="info"
              icon={<CheckCircleOutlineIcon fontSize="small" />}
              sx={{ mb: 3, borderRadius: "8px", fontSize: "0.825rem" }}
            >
              <strong>Approve</strong> to assign the PCC and notify the agency.{" "}
              <strong>Reject</strong> to send the rejection reason — the agency can resubmit.
            </Alert>

            <Box display="flex" gap={1.5}>
              <Button
                variant="contained"
                color="success"
                onClick={handleApprove}
                disabled={loading}
                startIcon={<CheckCircleOutlineIcon />}
                size="large"
              >
                {loading ? "Processing…" : "Approve & Submit PCC"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleReject}
                disabled={loading}
                startIcon={<CancelOutlinedIcon />}
                size="large"
              >
                {loading ? "Processing…" : "Reject Request"}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                disabled={loading}
                size="large"
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </DashboardLayout>
  );
}
